package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.data.dto.UserCardDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.widgets.CoinIcon
import com.arena_web.ui.components.widgets.GradientBorderBox
import com.arena_web.ui.components.widgets.formatCoins
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

/**
 * Confirm-purchase dialog for a card-shop template. Renders the card preview,
 * its price, and Cancel / Buy now actions. Pass `card = null` to keep it
 * dismissed. The caller is responsible for driving the [purchasing] flag and
 * surfacing [purchaseError] strings from the repo.
 */
@Composable
fun PurchaseCardModal(
    card: UserCardDto?,
    sportName: String,
    purchasing: Boolean,
    purchaseError: String?,
    onConfirm: (UserCardDto) -> Unit,
    onDismiss: () -> Unit,
) {
    if (card == null) return
    val sportColors = LocalSportColors.current
    val t = LocalAppStrings.current

    // Pre-check the wallet against the card price so we can surface the
    // top-up dialog instead of letting the backend reject the purchase with a
    // generic 400. Wallet balance is cached app-wide; first open kicks off a
    // fetch if it hasn't loaded yet.
    val container = LocalAppContainer.current
    val walletBalance by container.walletBalance.collectAsState()
    LaunchedEffect(card.id) { container.ensureWalletLoaded() }

    // Insufficient balance → open the wallet (mobile sheet / desktop popup).
    var walletOpen by remember(card.id) { mutableStateOf(false) }

    fun canAfford(): Boolean {
        val price = card.price.toDoubleOrNull() ?: return true
        val balance = walletBalance?.toDoubleOrNull() ?: return true
        return balance >= price
    }

    // The balance may not be loaded yet when Buy is tapped (canAfford is
    // optimistic), so the backend can still reject with "Insufficient balance"
    // (402). Catch that and open the wallet instead of just showing the error.
    LaunchedEffect(purchaseError) {
        if (purchaseError != null && purchaseError.contains("insufficient", ignoreCase = true)) {
            walletOpen = true
        }
    }

    val body: @Composable () -> Unit = {
        Column(
            modifier = Modifier.fillMaxWidth().sGap(20),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.purchaseCardTitle,
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(AppTypography.SemiBold)
                    .sLineHeight(26)
                    .color(AppColors.Text.Secondary),
            )

            Box(contentAlignment = Alignment.Center) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = sportName,
                    size = PlayerCardSize.Medium,
                    cardFrameUrl = card.cardImageUrl,
                    cardAssetsBasePath = card.assetsBasePath,
                    cardTextColor = card.textColor,
                    hideRating = true,
                )
            }

            Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
                CoinIcon(40)
                SpanText(
                    "× ${formatCoins(card.price)}",
                    modifier = Modifier
                        .sFontSize(22)
                        .fontWeight(AppTypography.SemiBold)
                        .sLineHeight(26)
                        .sLetterSpacing(0.22)
                        .color(sportColors.metricsPrimary),
                )
            }

            if (purchaseError != null) {
                SpanText(
                    purchaseError,
                    modifier = Modifier
                        .fillMaxWidth()
                        .sFontSize(14)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(22)
                        .color(AppColors.Text.OnSurfaceMuted),
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                ActionButton(
                    label = t.commonCancel,
                    primary = false,
                    enabled = !purchasing,
                    onClick = onDismiss,
                    modifier = Modifier.flexGrow(1),
                )
                ActionButton(
                    label = if (purchasing) t.purchaseCardBuying else t.purchaseCardBuyNow,
                    primary = true,
                    enabled = !purchasing,
                    onClick = {
                        if (canAfford()) onConfirm(card)
                        else walletOpen = true
                    },
                    modifier = Modifier.flexGrow(1),
                )
            }
        }
    }

    // Mobile → bottom sheet; desktop → centered modal.
    if (isMobile()) {
        BottomSheet(onDismiss = onDismiss, contentGap = 20) {
            body()
        }
    } else {
        Modal(
            isOpen = true,
            onClose = onDismiss,
            closeOnOverlayClick = !purchasing,
            title = null,
        ) {
            body()
        }
    }

    BuyCoinsDialog(
        isOpen = walletOpen,
        onClose = { walletOpen = false },
        walletBalance = walletBalance,
        zIndex = 1020,
    )
}

@Composable
private fun ActionButton(
    label: String,
    primary: Boolean,
    enabled: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val sportColors = LocalSportColors.current
    val interactionModifier = modifier
        .sHeight(46)
        .then(
            if (enabled) Modifier.cursor(Cursor.Pointer).onClick { onClick() }
            else Modifier.styleModifier { property("opacity", "0.6") },
        )
    val labelText: @Composable () -> Unit = {
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .color(if (primary) sportColors.onPrimary else AppColors.Text.Secondary),
        )
    }
    if (primary) {
        Box(
            modifier = interactionModifier
                .sBorderRadius(100)
                .backgroundColor(sportColors.primary),
            contentAlignment = Alignment.Center,
        ) { labelText() }
    } else {
        GradientBorderBox(
            modifier = interactionModifier,
            borderRadius = 100,
            background = AppGradients.InteractiveSecondary,
        ) { labelText() }
    }
}
